/* eslint-disable prettier/prettier */
import React from 'react';

import TimelineItem from 'components/TimelineItem';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelLogs, TravelDetails as TravelDetailsModel } from 'models/travelDetails';
import { mapPrices } from 'screens/components/Dashboard/mapper';
import { htmlT } from 'utils/generic';

import TravelDataDescription from '../TravelDataDescription';

interface Props {
  travelDetails: TravelDetailsModel;
  travelLog: TravelLogs;
  noSeparator?: boolean;
}

const Item = ({ travelDetails, travelLog, noSeparator }: Props) => {
  const { t } = useStoreTranslation();
  const { newTravelStatus, date, userName, message } = travelLog;
  const {
    air,
    prices: { justifyForBreakingRules },
  } = travelDetails;

  const ICON_TYPES = {
    ERROR: '/corporate/images/attention-icon.svg',
    CHECK: '/corporate/images/approved-icon.svg',
    TRAVEL: !!air ? '/corporate/images/mini-plane-circle.svg' : '/corporate/images/mini-hotel-circle.svg',
  };

  const status = {
    None: {
      title: t('travelDetails.solicitation.requested'),
      description: justifyForBreakingRules || '',
      icon: ICON_TYPES.TRAVEL,
    },
    Approved: {
      title: t('travelDetails.solicitation.approved', { user: userName }),
      icon: ICON_TYPES.CHECK,
    },
    Confirmed: {
      title: t('travelDetails.solicitation.confirmed'),
      description: t('travelDetails.solicitation.confirmedContent'),
      icon: ICON_TYPES.CHECK,
    },
    Paid: {
      title: t('travelDetails.solicitation.paid'),
      icon: ICON_TYPES.CHECK,
    },
    ErrorAtSending: {
      title: t('travelDetails.solicitation.errorSending'),
      icon: ICON_TYPES.ERROR,
    },
    Reproved: {
      title: t('travelDetails.solicitation.reproved', { user: userName }),
      icon: ICON_TYPES.ERROR,
    },
    PriceChanged: {
      title: t('travelDetails.solicitation.priceChanged'),
      description: travelLog.message
        ? htmlT(
          t,
          'travelDetails.solicitation.priceChangedDescription',
          mapPrices([`PriceChanged, ${travelLog.message}`], t('locale'), ','),
        )
        : '',
      icon: ICON_TYPES.ERROR,
    },
    PaymentFailure: {
      title: t('travelDetails.solicitation.paymentFailure'),
      icon: ICON_TYPES.ERROR,
    },
    Expired: {
      title: t('travelDetails.solicitation.expired'),
      icon: ICON_TYPES.ERROR,
    },
    Cancelled: {
      title: t('travelDetails.solicitation.cancelled'),
      icon: ICON_TYPES.ERROR,
    },
    Default: {
      title: newTravelStatus,
      icon: ICON_TYPES.TRAVEL,
    },
    TravelArrangerCreateTravel: {
      title: t('travelDetails.solicitation.travelArrangerCreateTravel', { user: userName }),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailRequestTravelSuccess: {
      title: t('travelDetails.solicitation.sendEmailRequestTravelSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailRequestTravelError: {
      title: t('travelDetails.solicitation.sendEmailRequestTravelError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    SendTravelForApproverEmailSuccess: {
      title: t('travelDetails.solicitation.sendTravelForApproverEmailSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendTravelForApproverEmailError: {
      title: t('travelDetails.solicitation.sendTravelForApproverEmailError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    SendEmailApproveTravelSuccess: {
      title: t('travelDetails.solicitation.sendEmailApproveTravelSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailApproveTravelError: {
      title: t('travelDetails.solicitation.sendEmailApproveTravelError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    SendEmailPaymentFailedSucces: {
      title: t('travelDetails.solicitation.sendEmailPaymentFailedSucces'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailPaymentFailedSuccess: {
      title: t('travelDetails.solicitation.sendEmailPaymentFailedSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailPaymentFailedError: {
      title: t('travelDetails.solicitation.sendEmailPaymentFailedError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    PayTravelSuccess: {
      title: t('travelDetails.solicitation.payTravelSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    PayTravelError: {
      title: t('travelDetails.solicitation.payTravelError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    TravelConfirmedNetactica: {
      title: t('travelDetails.solicitation.travelConfirmedNetactica'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailExpiredTravelSuccess: {
      title: t('travelDetails.solicitation.sendEmailExpiredTravelSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailExpiredTravelError: {
      title: t('travelDetails.solicitation.sendEmailExpiredTravelError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    SendEmailCancelledTravelSuccess: {
      title: t('travelDetails.solicitation.sendEmailCancelledTravelSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailCancelledTravelError: {
      title: t('travelDetails.solicitation.sendEmailCancelledTravelError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    SendEmailPendindTravelSuccess: {
      title: t('travelDetails.solicitation.sendEmailPendindTravelSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailPendindTravelError: {
      title: t('travelDetails.solicitation.sendEmailPendindTravelError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    SendEmailReprovedTravelSuccess: {
      title: t('travelDetails.solicitation.sendEmailReprovedTravelSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message
    },
    SendEmailReprovedTravelError: {
      title: t('travelDetails.solicitation.sendEmailReprovedTravelError'),
      icon: ICON_TYPES.ERROR,
      description: message
    },
    SendTravelForApproverWhatsappError: {
      title: t('travelDetails.solicitation.sendTravelForApproverWhatsappError'),
      icon: ICON_TYPES.ERROR,
      description: message,
    },
    SendTravelForApproverWhatsappSuccess: {
      title: t('travelDetails.solicitation.sendTravelForApproverWhatsappSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message,
    },
    SendVoucherSuccess: {
      title: t('travelDetails.solicitation.sendVoucherSuccess'),
      icon: ICON_TYPES.CHECK,
      description: message,
    },
    SendVoucherError: {
      title: t('travelDetails.solicitation.sendVoucherError'),
      icon: ICON_TYPES.ERROR,
      description: message,
    },
  };
  const timelineItemProps = {
    showHour: true,
    date,
    noSeparator,
    ...(status[newTravelStatus] || status.Default),
  };

  return !(newTravelStatus === 'PreApproved' || newTravelStatus === 'Pending') ? (
    <>
      {newTravelStatus === 'Confirmed' && (
        <TimelineItem
          showHour
          title={t('travelDetails.solicitation.travelData')}
          description={<TravelDataDescription data={travelDetails} />}
          icon={ICON_TYPES.TRAVEL}
          date={date}
        />
      )}
      <TimelineItem {...timelineItemProps} />
    </>
  ) : (
    <></>
  );
};

export default Item;
